// src/pages/admin/AdminDoctorList.tsx
import React, { useEffect, useState, useMemo } from 'react';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { FaEnvelope } from 'react-icons/fa';

interface Doctor {
  id: number;
  email: string | null;
  username: string;
  name: string;
  // ✅ 追加：Zoom 関連（後端が返さなくても undefined で動作）
  zoom_email?: string | null;
  zoom_verified?: boolean;
  zoom_user_id?: string | null;
  zoom_last_verified_at?: string | null; // ISO string
}

const ITEMS_PER_PAGE = 10;

const AdminDoctorList: React.FC = () => {
  const { t } = useTranslation();

  // 列表 & 分页
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  // 新增
  const [newDoctor, setNewDoctor] = useState({
    email: '',
    username: '',
    name: '',
    password: '',
  });
  const [adding, setAdding] = useState(false);

  // 编辑
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
  const [editingFields, setEditingFields] = useState<{
    email: string;
    username: string;
    name: string;
    // ✅ 追加：Zoom
    zoom_email: string;
  }>({
    email: '',
    username: '',
    name: '',
    zoom_email: '',
  });
  const [savingEdit, setSavingEdit] = useState(false);

  // 错误/提示
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [infoMsg, setInfoMsg] = useState<string | null>(null);
  const toast = (msg: string) => alert(msg);

  const fetchDoctors = async () => {
    try {
      const res = await api.get('/admin/doctors');
      setDoctors(res.data || []);
    } catch (err) {
      console.error('Failed to fetch doctors:', err);
      setErrorMsg(t('admin.fetch_failed') || '加载失败');
    }
  };

  useEffect(() => {
    fetchDoctors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 分页数据
  const totalPages = Math.ceil((doctors || []).length / ITEMS_PER_PAGE) || 1;
  const paginatedDoctors = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return (doctors || []).slice(start, start + ITEMS_PER_PAGE);
  }, [doctors, currentPage]);

  // 新增医生
  const handleAddDoctor = async () => {
    const { email, username, name, password } = newDoctor;
    if (!username.trim() || !name.trim() || !password.trim()) {
      toast(t('admin.fill_required_fields') || '必填项未填写');
      return;
    }
    setAdding(true);
    setErrorMsg(null);
    try {
      const payload: any = {
        username: username.trim(),
        name: name.trim(),
        password: password,
      };
      if (email.trim() !== '') {
        payload.email = email.trim(); // 有填才传
      }
      await api.post('/admin/doctors', payload);
      toast(t('admin.doctor_added') || '已添加');
      setNewDoctor({ email: '', username: '', name: '', password: '' });
      fetchDoctors();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || t('admin.add_failed') || '添加失败';
      setErrorMsg(String(msg));
      toast(String(msg));
    } finally {
      setAdding(false);
    }
  };

  // 删除医生
  const handleDelete = async (id: number) => {
    if (!window.confirm(t('admin.confirm_delete') || '本当に削除しますか？')) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast(t('admin.doctor_deleted') || '已删除');
      fetchDoctors();
    } catch (err) {
      console.error(err);
      toast(t('admin.delete_failed') || '删除失败');
    }
  };

  // 打开编辑
  const handleEdit = (doctor: Doctor) => {
    setEditingDoctor(doctor);
    setEditingFields({
      email: doctor.email || '',
      username: doctor.username,
      name: doctor.name,
      zoom_email: (doctor.zoom_email || '') as string,
    });
    setErrorMsg(null);
    setInfoMsg(null);
  };

  // 保存编辑（邮箱允许清空）——基础资料
  const handleSaveEdit = async () => {
    if (!editingDoctor) return;
    setSavingEdit(true);
    setErrorMsg(null);
    setInfoMsg(null);
    try {
      const payload: any = {
        username: editingFields.username.trim(),
        name: editingFields.name.trim(),
        email: editingFields.email.trim() === '' ? null : editingFields.email.trim(),
      };
      await api.put(`/admin/users/${editingDoctor.id}`, payload);
      toast(t('admin.edit_success') || '已保存');
      setEditingDoctor(null);
      fetchDoctors();
    } catch (err: any) {
      console.error(err);
      const msg = err?.response?.data?.detail || t('admin.edit_failed') || '保存失败';
      setErrorMsg(String(msg));
      toast(String(msg));
    } finally {
      setSavingEdit(false);
    }
  };

  // 重置密码
  const handleResetPassword = async (id: number) => {
    const newPassword = prompt(t('admin.enter_new_password') || '新しいパスワードを入力してください');
    if (!newPassword) return;
    try {
      await api.put(`/admin/users/${id}/reset-password`, { new_password: newPassword });
      toast(t('admin.password_reset_success') || 'パスワードを更新しました');
    } catch (err) {
      console.error(err);
      toast(t('admin.password_reset_failed') || 'パスワード更新に失敗しました');
    }
  };

  // ✅ 绑定并验证 Zoom（管理员代操作）
  const handleBindZoomAndVerify = async () => {
    if (!editingDoctor) return;
    const email = (editingFields.zoom_email || '').trim();
    if (!email) {
      toast(t('admin.zoom_email_required') || 'Zoomメールを入力してください');
      return;
    }
    setSavingEdit(true);
    setErrorMsg(null);
    setInfoMsg(null);
    try {
      // PUT /admin/doctors/{id}/zoom?verify=true
      const res = await api.put(`/admin/doctors/${editingDoctor.id}/zoom`, { zoom_email: email }, { params: { verify: true } });
      toast(t('admin.zoom_bind_ok') || 'Zoomメールを保存し、検証しました');
      // 关闭弹窗并刷新
      setEditingDoctor(null);
      fetchDoctors();
    } catch (err: any) {
      console.error(err);
      const msg = err?.response?.data?.detail || t('admin.zoom_bind_failed') || 'Zoom設定に失敗しました';
      setErrorMsg(String(msg));
      toast(String(msg));
    } finally {
      setSavingEdit(false);
    }
  };

  // ✅ 仅验证（不改邮箱）
  const handleVerifyZoomOnly = async () => {
    if (!editingDoctor) return;
    setSavingEdit(true);
    setErrorMsg(null);
    setInfoMsg(null);
    try {
      await api.post(`/admin/doctors/${editingDoctor.id}/zoom/verify`);
      toast(t('admin.zoom_verify_ok') || 'Zoom検証に成功しました');
      setEditingDoctor(null);
      fetchDoctors();
    } catch (err: any) {
      console.error(err);
      const msg = err?.response?.data?.detail || t('admin.zoom_verify_failed') || 'Zoom検証に失敗しました';
      setErrorMsg(String(msg));
      toast(String(msg));
    } finally {
      setSavingEdit(false);
    }
  };

  // 小组件：Zoom 状态显示
  const ZoomStatusBadge: React.FC<{ d: Doctor }> = ({ d }) => {
    const verified = !!d.zoom_verified;
    const email = d.zoom_email || '-';
    const ts = d.zoom_last_verified_at ? new Date(d.zoom_last_verified_at).toLocaleString() : null;
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
            verified ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {verified ? (t('admin.zoom_verified') || 'Verified') : (t('admin.zoom_unverified') || 'Unverified')}
          </span>
          <span className="text-xs text-gray-500">{email}</span>
        </div>
        {ts && <span className="text-[11px] text-gray-400 mt-0.5">{t('admin.last_verified_at') || '最終検証'}: {ts}</span>}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-4">
      {/* 顶部标题 */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{t('admin.doctor_management')}</h2>
          <p className="text-sm text-gray-500">{t('admin.doctor_management_desc') || '医師アカウントの追加・編集・削除 / Zoom 連携'}</p>
        </div>
      </div>

      {/* 错误/信息条 */}
      {(errorMsg || infoMsg) && (
        <div className={`mb-4 rounded-lg px-4 py-2 border ${errorMsg ? 'bg-red-50 text-red-700 border-red-200' : 'bg-coral/5 text-coral border-coral/20'}`}>
          {errorMsg || infoMsg}
        </div>
      )}

      {/* 新增卡片 */}
      <div className="bg-white shadow rounded-2xl p-5 mb-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg">➕ {t('admin.add_doctor')}</h3>
          <span className="text-xs text-gray-400">{t('admin.required_mark') || '* は必須'}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 text-sm font-medium">Email</label>
            <input
              className="border rounded-lg p-2.5 w-full focus:ring-2 focus:ring-blue-200"
              value={newDoctor.email}
              onChange={(e) => setNewDoctor({ ...newDoctor, email: e.target.value })}
              placeholder={t('optional') || '任意'}
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium">Username*</label>
            <input
              className="border rounded-lg p-2.5 w-full focus:ring-2 focus:ring-blue-200"
              value={newDoctor.username}
              onChange={(e) => setNewDoctor({ ...newDoctor, username: e.target.value })}
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium">{t('admin.full_name') || 'Full Name'}*</label>
            <input
              className="border rounded-lg p-2.5 w-full focus:ring-2 focus:ring-blue-200"
              value={newDoctor.name}
              onChange={(e) => setNewDoctor({ ...newDoctor, name: e.target.value })}
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium">Password*</label>
            <input
              type="password"
              className="border rounded-lg p-2.5 w-full focus:ring-2 focus:ring-blue-200"
              value={newDoctor.password}
              onChange={(e) => setNewDoctor({ ...newDoctor, password: e.target.value })}
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            className={`px-6 py-2.5 rounded-full text-white font-bold shadow-soft transition-all active:scale-95 ${adding ? 'bg-gray-400' : 'bg-coral hover:shadow-card hover:-translate-y-0.5'}`}
            onClick={handleAddDoctor}
            disabled={adding}
          >
            {adding ? (t('admin.saving') || '保存中…') : (t('admin.add_button') || '追加')}
          </button>
        </div>
      </div>

      {/* 列表卡片 */}
      <div className="bg-white shadow rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg">{t('admin.doctor_list')}</h3>
          <span className="text-xs text-gray-400">
            {t('admin.total_count', { n: doctors.length }) || `合計 ${doctors.length} 件`}
          </span>
        </div>

        <div className="overflow-x-auto rounded-xl border">
          {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
          <div className="md:hidden divide-y">
            {paginatedDoctors.map((doctor) => (
              <div key={doctor.id} className="p-4">
                {/* 氏名 + ID */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold break-words">{doctor.name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">ID：{doctor.id}</div>
                  </div>
                </div>

                {/* 各フィールド */}
                <div className="mt-3 space-y-2 text-sm">
                  <div className="break-all">
                    <span className="text-gray-400">Email：</span>
                    <span className="text-gray-700">{doctor.email || '-'}</span>
                  </div>
                  <div className="break-all">
                    <span className="text-gray-400">Username：</span>
                    <span className="text-gray-700">{doctor.username}</span>
                  </div>
                  {/* ログイン方法 */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-gray-400">{t('admin.login_method') || 'ログイン方法'}：</span>
                    <span className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-blue-50 text-blue-600 font-semibold border border-blue-100">
                      <FaEnvelope size={12} /> {t('admin.login_email') || 'メール/パスワード'}
                    </span>
                  </div>
                  {/* Zoom 状態 */}
                  <div className="flex items-start gap-1.5 flex-wrap">
                    <span className="text-gray-400 shrink-0">Zoom：</span>
                    <div className="min-w-0">
                      <ZoomStatusBadge d={doctor} />
                    </div>
                  </div>
                </div>

                {/* 操作 */}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    className="px-4 py-1.5 rounded-full bg-white border border-coral/30 text-coral hover:bg-coral/5 text-xs font-bold shadow-soft transition-all active:scale-95"
                    onClick={() => handleEdit(doctor)}
                  >
                    {t('admin.edit_button')}
                  </button>
                  <button
                    className="px-4 py-1.5 rounded-full bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 text-xs font-bold shadow-soft transition-all active:scale-95"
                    onClick={() => handleResetPassword(doctor.id)}
                  >
                    {t('admin.reset_password')}
                  </button>
                  <button
                    className="px-4 py-1.5 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50 text-xs font-bold shadow-soft transition-all active:scale-95"
                    onClick={() => handleDelete(doctor.id)}
                  >
                    {t('admin.delete_button')}
                  </button>
                </div>
              </div>
            ))}
            {doctors.length === 0 && (
              <div className="p-6 text-center text-gray-500">
                {t('admin.no_doctors')}
              </div>
            )}
          </div>

          {/* ▼ デスクトップ: テーブル表示 */}
          <table className="hidden md:table w-full table-auto">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-700">
                <th className="p-3 text-xs md:text-sm">ID</th>
                <th className="p-3 text-xs md:text-sm">{t('admin.full_name') || 'Name'}</th>
                <th className="p-3 text-xs md:text-sm">Email</th>
                <th className="p-3 text-xs md:text-sm">Username</th>
                {/* 管理人员视角看登录方式 */}
                <th className="p-3 text-xs md:text-sm">{t('admin.login_method') || 'ログイン方法'}</th>
                {/* ✅ Zoom 状态列 */}
                <th className="p-3 text-xs md:text-sm">Zoom</th>
                <th className="p-3 text-xs md:text-sm">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDoctors.map((doctor) => (
                <tr key={doctor.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 text-sm">{doctor.id}</td>
                  <td className="p-3 text-sm">{doctor.name}</td>
                  <td className="p-3 text-sm">{doctor.email || '-'}</td>
                  <td className="p-3 text-sm">{doctor.username}</td>
                  <td className="p-3 text-sm">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-blue-50 text-blue-600 font-semibold border border-blue-100">
                      <FaEnvelope size={12} /> {t('admin.login_email') || 'メール/パスワード'}
                    </span>
                  </td>
                  <td className="p-3 text-sm">
                    <ZoomStatusBadge d={doctor} />
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="px-4 py-1.5 rounded-full bg-white border border-coral/30 text-coral hover:bg-coral/5 text-xs md:text-sm font-bold shadow-soft transition-all active:scale-95"
                        onClick={() => handleEdit(doctor)}
                      >
                        {t('admin.edit_button')}
                      </button>
                      <button
                        className="px-4 py-1.5 rounded-full bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 text-xs md:text-sm font-bold shadow-soft transition-all active:scale-95"
                        onClick={() => handleResetPassword(doctor.id)}
                      >
                        {t('admin.reset_password')}
                      </button>
                      <button
                        className="px-4 py-1.5 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50 text-xs md:text-sm font-bold shadow-soft transition-all active:scale-95"
                        onClick={() => handleDelete(doctor.id)}
                      >
                        {t('admin.delete_button')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {doctors.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-500">
                    {t('admin.no_doctors')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 flex-wrap gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 flex items-center justify-center rounded-full font-bold transition-all shadow-soft ${page === currentPage
                  ? 'bg-coral text-white shadow-card animate-in fade-in duration-300'
                  : 'bg-white border border-border text-gray-500 hover:bg-gray-50 hover:text-coral'
                  }`}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 编辑弹窗 */}
      {editingDoctor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold">{t('admin.editing_doctor')}</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setEditingDoctor(null)}
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">ID: {editingDoctor.id}</p>

            <div className="space-y-4">
              {/* 基础资料 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium">Email</label>
                  <div className="flex gap-2">
                    <input
                      className="border rounded-lg p-2.5 w-full focus:ring-2 focus:ring-blue-200"
                      value={editingFields.email}
                      onChange={(e) => setEditingFields({ ...editingFields, email: e.target.value })}
                      placeholder={t('optional') || '任意'}
                    />
                    <button
                      type="button"
                      onClick={() => setEditingFields({ ...editingFields, email: '' })}
                      className="px-3 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm whitespace-nowrap inline-flex items-center"
                      title={t('admin.clear_email') || 'メールを空にする'}
                    >
                      {t('admin.clear') || 'クリア'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('admin.clear_email_hint') || '空にして保存するとメールは未設定になります'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium">Username</label>
                  <input
                    className="border rounded-lg p-2.5 w-full focus:ring-2 focus:ring-blue-200"
                    value={editingFields.username}
                    onChange={(e) => setEditingFields({ ...editingFields, username: e.target.value })}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium">{t('admin.full_name') || 'Full Name'}</label>
                  <input
                    className="border rounded-lg p-2.5 w-full focus:ring-2 focus:ring-blue-200"
                    value={editingFields.name}
                    onChange={(e) => setEditingFields({ ...editingFields, name: e.target.value })}
                  />
                </div>
              </div>

              {/* ✅ Zoom 配置 */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">{t('admin.zoom_settings') || 'Zoom 設定'}</h3>
                  <ZoomStatusBadge d={editingDoctor} />
                </div>
                <label className="block text-sm font-medium">Zoom Email</label>
                <div className="flex gap-2">
                  <input
                    className="border rounded-lg p-2.5 w-full focus:ring-2 focus:ring-blue-200"
                    value={editingFields.zoom_email}
                    onChange={(e) => setEditingFields({ ...editingFields, zoom_email: e.target.value })}
                    placeholder="doctor.zoom@example.com"
                  />
                  <button
                    type="button"
                    onClick={() => setEditingFields({ ...editingFields, zoom_email: '' })}
                    className="px-3 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm whitespace-nowrap inline-flex items-center"
                  >
                    {t('admin.clear') || 'クリア'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin.zoom_hint') || 'Zoom 組織配下の医師メールを設定し、存在性を検証します（S2S OAuth）。'}
                </p>

                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    className={`px-5 py-2.5 rounded-full text-white font-bold shadow-soft transition-all active:scale-95 ${savingEdit ? 'bg-gray-400' : 'bg-teal-600 hover:bg-teal-700 hover:shadow-card'}`}
                    onClick={handleBindZoomAndVerify}
                    disabled={savingEdit}
                    title="PUT /admin/doctors/{id}/zoom?verify=true"
                  >
                    {savingEdit ? (t('processing') || '处理中…') : (t('admin.zoom_bind_and_verify') || '保存并验证')}
                  </button>
                  <button
                    className="px-5 py-2.5 rounded-full bg-white border border-teal-200 text-teal-700 hover:bg-teal-50 font-bold shadow-soft transition-all active:scale-95"
                    onClick={handleVerifyZoomOnly}
                    disabled={savingEdit}
                    title="POST /admin/doctors/{id}/zoom/verify"
                  >
                    {t('admin.zoom_verify_only') || '仅验证'}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
                onClick={() => setEditingDoctor(null)}
              >
                {t('admin.cancel_button')}
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-white ${savingEdit ? 'bg-gray-400' : 'bg-coral hover:opacity-90'}`}
                onClick={handleSaveEdit}
                disabled={savingEdit}
              >
                {savingEdit ? (t('processing') || '处理中…') : t('admin.save_button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDoctorList;
